/**
 * Language Selector Component
 * Sélecteur de langue FR/EN avec drapeau rond et cercle doré
 */
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import config from 'config';

// Flag SVG components for crisp rendering
const FlagFR = () => (
  <svg viewBox="0 0 36 36" className="w-full h-full">
    <rect fill="#002395" width="12" height="36"/>
    <rect fill="#FFFFFF" x="12" width="12" height="36"/>
    <rect fill="#ED2939" x="24" width="12" height="36"/>
  </svg>
);

const FlagGB = () => (
  <svg viewBox="0 0 36 36" className="w-full h-full">
    <rect fill="#00247D" width="36" height="36"/>
    <path fill="#FFFFFF" d="M0,0 L36,36 M36,0 L0,36" stroke="#FFFFFF" strokeWidth="6"/>
    <path fill="#CF142B" d="M0,0 L36,36 M36,0 L0,36" stroke="#CF142B" strokeWidth="2"/>
    <rect fill="#FFFFFF" x="15" width="6" height="36"/>
    <rect fill="#FFFFFF" y="15" width="36" height="6"/>
    <rect fill="#CF142B" x="16.5" width="3" height="36"/>
    <rect fill="#CF142B" y="16.5" width="36" height="3"/>
  </svg>
);

const FlagDE = () => (
  <svg viewBox="0 0 36 36" className="w-full h-full">
    <rect fill="#000000" width="36" height="12"/>
    <rect fill="#DD0000" y="12" width="36" height="12"/>
    <rect fill="#FFCC00" y="24" width="36" height="12"/>
  </svg>
);

const FlagES = () => (
  <svg viewBox="0 0 36 36" className="w-full h-full">
    <rect fill="#C60B1E" width="36" height="9"/>
    <rect fill="#FFC400" y="9" width="36" height="18"/>
    <rect fill="#C60B1E" y="27" width="36" height="9"/>
  </svg>
);

const languages = [
  { code: 'fr', label: 'Français', Flag: FlagFR },
  { code: 'en', label: 'English', Flag: FlagGB },
  { code: 'de', label: 'Deutsch', Flag: FlagDE },
  { code: 'es', label: 'Español', Flag: FlagES }
];

// Round flag with golden border component
const RoundFlag = ({ Flag, isActive = false, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10'
  };
  
  return (
    <div 
      className={`${sizeClasses[size]} rounded-full overflow-hidden ring-2 ${
        isActive ? 'ring-[#C9A24D]' : 'ring-[#C9A24D]/30'
      } transition-all duration-200`}
    >
      <Flag />
    </div>
  );
};

const LanguageSelector = ({ variant = 'default' }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  
  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
    setIsOpen(false);
    
    // Also update user preference if logged in
    const token = localStorage.getItem('access_token');
    if (token) {
      fetch(`${config.API_URL}/api/auth/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ language: langCode })
      }).catch(console.error);
    }
  };
  
  // Minimal variant - just the round flag
  if (variant === 'minimal') {
    return (
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
          data-testid="language-selector"
          aria-label="Changer de langue"
        >
          <RoundFlag Flag={currentLang.Flag} isActive={true} size="sm" />
          <ChevronDown className={`w-3 h-3 text-[#C9A24D] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <div className="absolute right-0 mt-3 w-44 bg-[#1F1F1F] border border-[#C9A24D]/30 shadow-2xl z-50 rounded-lg overflow-hidden">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-all ${
                  lang.code === currentLang.code 
                    ? 'text-[#C9A24D] bg-[#C9A24D]/10' 
                    : 'text-[#E5E5E5]/70 hover:text-[#C9A24D] hover:bg-[#C9A24D]/5'
                }`}
              >
                <RoundFlag 
                  Flag={lang.Flag} 
                  isActive={lang.code === currentLang.code} 
                  size="sm" 
                />
                <span className="font-medium">{lang.label}</span>
                {lang.code === currentLang.code && (
                  <Check className="w-4 h-4 ml-auto text-[#C9A24D]" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
  
  // Default variant with larger flag
  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-1 hover:bg-[#C9A24D]/5 rounded-lg transition-colors"
        data-testid="language-selector"
        aria-label="Changer de langue"
      >
        <RoundFlag Flag={currentLang.Flag} isActive={true} size="md" />
        <ChevronDown className={`w-4 h-4 text-[#C9A24D] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-3 w-48 bg-[#1F1F1F] border border-[#C9A24D]/30 shadow-2xl z-50 rounded-lg overflow-hidden">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-3.5 text-sm transition-all ${
                lang.code === currentLang.code 
                  ? 'text-[#C9A24D] bg-[#C9A24D]/10' 
                  : 'text-[#E5E5E5]/70 hover:text-[#C9A24D] hover:bg-[#C9A24D]/5'
              }`}
            >
              <RoundFlag 
                Flag={lang.Flag} 
                isActive={lang.code === currentLang.code} 
                size="md" 
              />
              <span className="font-medium">{lang.label}</span>
              {lang.code === currentLang.code && (
                <Check className="w-4 h-4 ml-auto text-[#C9A24D]" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
